import { useCallback, useEffect, useState } from 'react';
import { getFinanceTransactions, type FinanceTransaction } from '../api.js';
import type { FlowDrill } from '../components/CashFlowSankey.js';
import { EmptyState, Group, Row, Sheet } from '../ui/index.js';
import CategoryPicker from './CategoryPicker.js';
import { dayLabel, money } from './format.js';
import { SkeletonGroup } from './parts.js';
import { useCategoryTree } from './tree.js';

/**
 * Everything behind one node of the cash-flow picture in the current window:
 * the rows filed under its categories (and below them), or paid by its payers —
 * what a tap on any Sankey node or ribbon, or a "Where it went" row, opens. Each
 * row can be re-filed from here, since a surprise in the list is usually a mis-file.
 */
export default function CategoryDrill({
  drill,
  range,
  onClose,
  onChange,
}: {
  drill: FlowDrill;
  range: { from: string; to: string };
  onClose: () => void;
  onChange: () => void;
}): JSX.Element {
  const cats = useCategoryTree();
  const [rows, setRows] = useState<FinanceTransaction[] | null>(null);
  const [editing, setEditing] = useState<FinanceTransaction | null>(null);

  // The one category this sheet is about, when there is one (for the child labels).
  const category = drill.categories?.length === 1 ? drill.categories[0]! : null;

  const load = useCallback(async () => {
    const base = { from: range.from, to: range.to, limit: 1000 };
    const queries = [
      ...(drill.categories ?? []).map((c) => getFinanceTransactions({ ...base, category: c })),
      ...(drill.names ?? []).map((n) => getFinanceTransactions({ ...base, search: n })),
    ];
    const seen = new Set<string>();
    const txns = (await Promise.all(queries)).flat().filter((t) => !seen.has(t.id) && seen.add(t.id));
    // Same rows the chart counts: settled, outside transfers, on the node's side.
    const side = (t: FinanceTransaction) =>
      drill.direction === 'in' ? t.amountCents > 0 : drill.direction === 'out' ? t.amountCents < 0 : true;
    // A payer search matches text anywhere; keep only that payer's own rows.
    const named = (t: FinanceTransaction) => !drill.names || drill.names.includes(t.merchant ?? t.description);
    setRows(
      txns
        .filter((t) => !t.pending && !t.isTransfer && side(t) && (drill.categories ? true : named(t)))
        .sort((a, b) => b.postedAt.localeCompare(a.postedAt)),
    );
  }, [drill, range]);

  useEffect(() => {
    void load();
  }, [load]);

  // One sheet at a time: the picker replaces the list, then hands back to it.
  if (editing) {
    return (
      <CategoryPicker
        txn={editing}
        onClose={() => setEditing(null)}
        onPicked={() => {
          setEditing(null);
          void load();
          onChange();
        }}
      />
    );
  }

  // Money in reads as a positive total; spending as what left.
  const total = (rows ?? []).reduce((s, t) => s + (drill.direction === 'in' ? t.amountCents : -t.amountCents), 0);
  const title = category === 'uncategorized' ? 'Uncategorized' : category ? cats.full(category) : drill.title;

  return (
    <Sheet title={title} onClose={onClose}>
      {rows === null ? (
        <SkeletonGroup />
      ) : rows.length === 0 ? (
        <EmptyState icon="receipt" title="Nothing here" hint="No settled transactions here for this window." />
      ) : (
        <Group
          header={`${rows.length} ${rows.length === 1 ? 'transaction' : 'transactions'} · ${money(total)}`}
          footer="Tap one to move it to another category."
        >
          {rows.map((t) => {
            // Under a parent, show which child each row sits in.
            const sub =
              t.category && t.category !== category
                ? category
                  ? cats.full(t.category).split(' › ').slice(category.split('.').length).join(' › ')
                  : cats.full(t.category)
                : null;
            return (
              <Row
                key={t.id}
                title={t.merchant ?? t.description}
                sub={[dayLabel(t.postedAt.slice(0, 10)), sub].filter(Boolean).join(' · ')}
                detail={
                  <span className={`hb-amount${t.amountCents > 0 ? ' hb-amount--in' : ''}`}>
                    {money(t.amountCents, { sign: t.amountCents > 0 })}
                  </span>
                }
                onClick={() => setEditing(t)}
                ariaLabel={`${t.merchant ?? t.description}, ${money(t.amountCents)}. Change category`}
              />
            );
          })}
        </Group>
      )}
    </Sheet>
  );
}
